import type { EnumDef } from '@mss_creator/shared';
import { Alert, Icon, LoadingRing } from '@pms-digital/design-system';
import { OptionTiles } from './option-tiles';
import { SettingRow } from './setting-row';

interface FlagRowProps {
  def: EnumDef;
  /** The value to show: the server's, or the one being saved. */
  value: string;
  pending: boolean;
  saved: boolean;
  /** Set when saving failed: the previous value is shown again, with this reason. */
  errorMessage?: string | undefined;
  onSelect: (value: string) => void;
}

/**
 * One backend flag: plain option tiles (no miniature: a flag has no visual
 * effect to show) with the "Tous les utilisateurs" scope label. Applies on
 * click; the row says "Enregistré" afterwards, or shows why the write failed.
 * The mutation lives in the parent so a flag that needs a confirmation
 * (Tahiti) can intercept `onSelect`.
 */
export function FlagRow({ def, value, pending, saved, errorMessage, onSelect }: FlagRowProps) {
  return (
    <SettingRow
      label={def.label}
      description={def.description}
      scope="global"
      modified={value !== def.default}
      onReset={() => onSelect(def.default)}
    >
      <OptionTiles
        label={def.label}
        options={def.options}
        value={value}
        disabled={pending}
        onChange={onSelect}
      />
      {pending && (
        <p className="mt-2 inline-flex items-center gap-2 font-semibold text-gray-600 text-small">
          <LoadingRing size="sm" />
          Enregistrement…
        </p>
      )}
      {saved && !pending && (
        <output className="mt-2 flex items-center gap-1 font-semibold text-small text-vert-deep">
          <Icon name="check" size={16} />
          Enregistré
        </output>
      )}
      {errorMessage && (
        <div className="mt-3 max-w-[560px]">
          <Alert tone="danger">{errorMessage}</Alert>
        </div>
      )}
    </SettingRow>
  );
}
